import React, { useState, useRef, useEffect } from 'react';
import {
  TextField,
  InputAdornment,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Paper,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorIcon from '@mui/icons-material/Error';
import palette from 'src/theme/palette';
import { LoadingButton } from '@mui/lab';
import LoadingButtonWrapper from './LoadingButtonWrapper';

const FilterMultiDropdown = ({
  label,
  name,
  value = [],
  onChange,
  onBlur,
  error,
  helperText,
  placeholder = 'Select options',
  backgroundColor,
  disabled,
  fontFamily,
  marginBottom,
  height,
  fontSize,
  color,
  labelColor,
  width,
  options = [],
  showNameTag,
  labelFontMedium,
  onClickDone,
  onOpen,
  onClose
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (isOpen) onOpen?.();
  }, [isOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        if (isOpen) {
          onClose?.();
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Filtered list based on search
  const filteredOptions = options.filter(
    (option) =>
      option.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Toggle selection
  const handleToggle = (optionValue) => {
    const currentIndex = value.indexOf(optionValue);
    const newChecked = [...value];
    if (currentIndex === -1) newChecked.push(optionValue);
    else newChecked.splice(currentIndex, 1);

    onChange?.({
      target: { name, value: newChecked }
    });
  };

  // Select / Deselect All
  const handleSelectAll = () => {
    if (value.length === filteredOptions.length) {
      const newValue = value.filter(
        (val) => !filteredOptions.some((option) => option.value === val)
      );
      onChange?.({ target: { name, value: newValue } });
    } else {
      const allFilteredValues = filteredOptions.map((o) => o.value);
      onChange?.({
        target: { name, value: [...new Set([...value, ...allFilteredValues])] }
      });
    }
  };

  // Avatar color helpers
  const stringToColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return `hsl(${hash % 360}, 75%, 65%)`;
  };

  const stringToColorText = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return `hsl(${hash % 360}, 25%, 35%)`;
  };

  return (
    <div
      ref={dropdownRef}
      style={{
        marginBottom: error ? '0.2rem' : marginBottom || '1rem',
        position: 'relative',
        width: width || '100%'
      }}
    >
      {/* Label */}
      {label && (
        <Typography
          sx={{
            fontSize: 12,
            marginBottom: '4px',
            fontFamily: labelFontMedium ? 'poppinsMedium' : 'poppinsRegular',
            color: labelColor || '#1a1a1a'
          }}
        >
          {label}
        </Typography>
      )}

      {/* Dropdown trigger */}
      <Box
        onClick={() => {
          if (disabled) return;
          if (!isOpen) {
            onOpen?.(); // 🔹 Notify parent when opening
          } else {
            onClose?.(); // 🔹 Notify parent when closing
          }
          setIsOpen(!isOpen);
        }}
        sx={{
          border: `1.5px solid ${
            error ? palette.primary.inputbordererror : palette.primary.inputborder
          }`,
          borderRadius: '6px',
          padding: '4px 14px',
          minHeight: height || 40,
          backgroundColor: backgroundColor || 'transparent',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          '&:hover': {
            borderColor: error ? palette.primary.inputbordererror : palette.primary.inputborder
          }
        }}
      >
        {/* Placeholder / Selected Count */}
        {value.length === 0 ? (
          <Typography
            sx={{
              fontSize: 12,
              color: '#9e9e9e',
              fontFamily: fontFamily || 'poppinsRegular'
            }}
          >
            {placeholder}
          </Typography>
        ) : (
          <Typography
            sx={{
              fontSize: 12,
              color: '#333',
              fontFamily: fontFamily || 'poppinsRegular'
            }}
          >
            {`${value.length} selected`}
          </Typography>
        )}

        <InputAdornment position="end" sx={{ ml: 'auto' }}>
          {error ? (
            <ErrorIcon color="error" sx={{ fontSize: 20 }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 20, color: '#646464' }} />
          )}
        </InputAdornment>
      </Box>

      {/* Error Text */}
      {error && (
        <Typography
          sx={{
            fontFamily: 'poppinsRegular',
            fontSize: 11,
            marginTop: '4px',
            color: '#7F1D1D'
          }}
        >
          {helperText}
        </Typography>
      )}

      {/* Dropdown Menu */}
      {isOpen && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1300,
            mt: 0.5,
            maxHeight: 300,
            overflow: 'auto',
            border: `1px solid ${palette.primary.inputborder}`,
            borderRadius: '6px'
          }}
        >
          {/* Search */}
          <Box sx={{ p: 1 }}>
            <TextField
              fullWidth
              autoFocus
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: 12,
                  height: 32
                }
              }}
            />
          </Box>

          <Divider />

          {/* Select All */}
          {filteredOptions.length > 0 && (
            <>
              <ListItem button onClick={handleSelectAll} sx={{ py: 0, px: 0 }}>
                <Checkbox
                  size="small"
                  checked={
                    value.length > 0 && filteredOptions.every((opt) => value.includes(opt.value))
                  }
                  indeterminate={
                    value.length > 0 &&
                    filteredOptions.some((opt) => value.includes(opt.value)) &&
                    !filteredOptions.every((opt) => value.includes(opt.value))
                  }
                />
                <ListItemText
                  primary="Check All"
                  sx={{
                    '& .MuiTypography-root': {
                      fontSize: 12,
                      fontFamily: 'poppinsMedium'
                    }
                  }}
                />
              </ListItem>
              <Divider />
            </>
          )}

          {/* Options */}
          <List sx={{ py: 0, maxHeight: 160, overflow: 'auto' }}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <ListItem
                  key={option.value}
                  button
                  onClick={() => handleToggle(option.value)}
                  sx={{ py: 0, px: 0 }}
                >
                  <Checkbox size="small" checked={value.includes(option.value)} />
                  {showNameTag && (
                    <Box
                      sx={{
                        height: 22,
                        width: 22,
                        mr: 1,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontFamily: 'poppinsMedium',
                        color: stringToColorText(option.name),
                        textTransform: 'uppercase',
                        background: stringToColor(option.name)
                      }}
                    >
                      {option.name?.[0] || ''}
                    </Box>
                  )}
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontSize: 12,
                          textTransform: 'capitalize',
                          fontFamily: 'poppinsRegular'
                        }}
                      >
                        {option.name}
                      </Typography>
                    }
                  />
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText
                  primary="No options found"
                  sx={{
                    textAlign: 'center',
                    '& .MuiTypography-root': {
                      fontSize: 12,
                      fontFamily: 'poppinsRegular',
                      color: palette.primary.disabled
                    }
                  }}
                />
              </ListItem>
            )}
          </List>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              pr: 1,
              py: 1,
              borderTop: '1px solid #eee'
            }}
          >
            <LoadingButtonWrapper
              onClick={() => {
                onClickDone?.(); // parent callback
                setIsOpen(false); // ✅ close dropdown locally
                onClose?.(); // ✅ trigger parent's onClose logic
              }}
              variant="contained"
              sx={{
                width: 70,
                height: 24,
                fontSize: 10,
                borderRadius: 0.7,
                fontFamily: 'poppinsLight',
                letterSpacing: 0.3,
                minWidth: 10,
                paddingRight: 0,
                paddingLeft: 0,
                paddingTop: 0,
                paddingBottom: 0,
                mr: 0.5,
                transition: 'width 0.3s ease, background-color 0.3s ease, transform 0.3s ease'
              }}
              title="Done"
            />
          </Box>
        </Paper>
      )}
    </div>
  );
};

export default FilterMultiDropdown;
